import { Divider } from 'component-library';
import { useTranslation } from 'react-i18next';

import { BenchmarkType, PlotReportFactLabelNameEnum, R1FactType } from '@/api/rest/resources/types/fact';
import { GraphTileType } from '@/api/rest/resources/types/widget';
import { GraphFact } from '@/components/Charts/types';
import { NCCard, NCCard2SectionLayout, NCCardComplianceFooter, NCCardHeader } from '@/components/NCCard/NCCard';
import { NDVIExplainer } from '@/components/NCExplainers/NDVIExplainer';
import { COMPLIANCE_LOGOS_NDVI } from '@/config/constants';
import { useBenchmarkSelection } from '@/hooks/useBenchmarkSelection';
import { useGetCustomBenchmarks } from '@/hooks/useCustomBenchmarks';
import { getBenchmarksFromFacts, useBenchmarksForPlot } from '@/pages/shared/hooks/useBenchmarkForFact';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';
import { usePlotFacts } from '@/pages/shared/hooks/usePlotFact';
import { getFactFromFactArray } from '@/pages/shared/hooks/utils';
import { isNullish } from '@/utils/booleans';

import { PlotCardWrapper } from '../PlotCardWrapper';
import { NDVIGraphTile } from './GraphTile';
import { NDVIStatsTile } from './StatsTile';

export const NDVI = () => {
  const { t } = useTranslation();

  return (
    <PlotCardWrapper name={t('shared.ncaDetail.details.protectedAreas.labels.protectedAreas')}>
      <NDVICard />
    </PlotCardWrapper>
  );
};

const NDVICard = () => {
  const { t } = useTranslation();

  const factNames = {
    graph: R1FactType.vegetation_condition_ndvi_historic_graph,
    latestAnalysis: R1FactType.vegetation_condition_ndvi,
    oneYearUplift: R1FactType.vegetation_condition_ndvi_previous_1y_uplift,
    trend: R1FactType.vegetation_condition_ndvi_previous_1y_pct_change,
    benchmark: R1FactType.vegetation_condition_ndvi_benchmark_graph,
  };

  const facts = usePlotFacts(Object.values(factNames));

  const graphData = getFactFromFactArray<GraphFact[]>(facts, factNames.graph)?.value;
  const latestAnalysisFact = getFactFromFactArray(facts, factNames.latestAnalysis);
  const oneYearUpliftFact = getFactFromFactArray(facts, factNames.oneYearUplift);
  const trendFact = getFactFromFactArray(facts, factNames.trend);

  const highBenchmarkFact = getFactFromFactArray<GraphFact[]>(facts, factNames.benchmark, {
    [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.high,
  });
  const moderateBenchmarkFact = getFactFromFactArray<GraphFact[]>(facts, factNames.benchmark, {
    [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.moderate,
  });
  const lowBenchmarkFact = getFactFromFactArray<GraphFact[]>(facts, factNames.benchmark, {
    [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.low,
  });

  const graphBenchmarks = getBenchmarksFromFacts({
    highBenchmarkFact,
    moderateBenchmarkFact,
    lowBenchmarkFact,
  });

  const statsBenchmarks = useBenchmarksForPlot(factNames.benchmark);

  const organizationId = useOrganizationId();
  const indicator = GraphTileType.ndvi;
  const [selectedBenchmarkId, setSelectedBenchmarkId] = useBenchmarkSelection(indicator);

  const { data: customBenchmarksResp } = useGetCustomBenchmarks(organizationId, indicator);

  const activeCustomBenchmark = customBenchmarksResp?.results?.find((b) => b.id === selectedBenchmarkId);

  const hasChartData = !isNullish(graphData);
  const hasStatsData = !isNullish(latestAnalysisFact?.value) || !isNullish(oneYearUpliftFact?.value);

  if (!hasChartData && !hasStatsData) {
    return null;
  }

  return (
    <NCCard explainer={<NDVIExplainer />} data-testid='ndvi-nca'>
      <NCCardHeader
        title={t('shared.ncaDetail.details.ndvi.labels.vegetationCondition')}
        className='lg:grid-cols-[1fr_1fr]'
      />
      <NCCard2SectionLayout>
        <NDVIGraphTile
          data={graphData}
          benchmarks={graphBenchmarks}
          highBenchmark={highBenchmarkFact}
          moderateBenchmark={moderateBenchmarkFact}
          lowBenchmark={lowBenchmarkFact}
          activeCustomBenchmark={activeCustomBenchmark}
          selectedBenchmarkId={selectedBenchmarkId}
          onBenchmarkSelect={setSelectedBenchmarkId}
        />
        <NDVIStatsTile
          latestAnalysisFact={latestAnalysisFact}
          oneYearUpliftFact={oneYearUpliftFact}
          trendFact={trendFact}
          benchmarks={statsBenchmarks}
        />
      </NCCard2SectionLayout>
      <Divider />
      <NCCardComplianceFooter logos={COMPLIANCE_LOGOS_NDVI} />
    </NCCard>
  );
};
